import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import InputForm from './components/InputForm';
import RoutesList from './components/RoutesList';
import AgentSelector from './components/AgentSelector';
import AgentsManager from './components/AgentsManager';
import Settings from './components/Settings';
import Login from './components/Login';
import { 
  optimizeRoutes, 
  testMapboxConnection, 
  getAgents, 
  getOptimizationJobs, 
  isAuthenticated, 
  getCurrentUser, 
  logout,
  // Admin API functions
  listTenants,
  createTenant,
  updateTenant,
  listTenantUsers,
  createTenantUser,
  updateTenantUser,
  resetTenantUserPassword
} from './services/api';

// Importar componente de mapa condicional (Map ou MapPlaceholder)
const IS_ADMIN = process.env.REACT_APP_ADMIN === 'true';
const MapComponent = IS_ADMIN 
  ? React.lazy(() => import('./components/MapPlaceholder'))
  : React.lazy(() => import('./components/Map'));

// Obter configurações do localStorage ou usar valores padrão
const getStoredSettings = () => {
  try {
    const storedSettings = localStorage.getItem('rotasmais_settings');
    return storedSettings ? JSON.parse(storedSettings) : {
      defaultCity: '',
      defaultState: '',
      defaultCountry: 'Brasil'
    };
  } catch (e) {
    console.error('Erro ao carregar configurações:', e);
    return {
      defaultCity: '',
      defaultState: '',
      defaultCountry: 'Brasil'
    };
  }
};

// Componente de proteção para rotas que exigem autenticação
const PrivateRoute = ({ children }) => {
  return isAuthenticated() ? children : <Navigate to="/login" />;
};

function App() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [mapboxStatus, setMapboxStatus] = useState(null);
  const [currentScreen, setCurrentScreen] = useState('home'); // 'home', 'agents', 'settings', 'history'
  const [allAgents, setAllAgents] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [settings, setSettings] = useState(getStoredSettings());
  const [emptyMap, setEmptyMap] = useState(true);
  const [optimizationHistory, setOptimizationHistory] = useState([]);
  const [user, setUser] = useState(getCurrentUser());
  
  // Carregar lista inicial de agentes ao montar o componente
  useEffect(() => {
    if (isAuthenticated()) {
      console.log('App: Carregando agentes iniciais');
      const agentsLoaded = localStorage.getItem('agents_loaded');
      
      // Se for a primeira carga, ou se não temos agentes, carregar
      if (!agentsLoaded || allAgents.length === 0) {
        loadAgents();
        localStorage.setItem('agents_loaded', 'true');
      }
    }
  }, []);
  
  // Carregar histórico quando a tela de histórico for exibida
  useEffect(() => {
    if (currentScreen === 'history' && isAuthenticated()) {
      loadOptimizationHistory();
    }
  }, [currentScreen]);

  const loadAgents = async () => {
    try {
      const response = await getAgents();
      if (response.success) {
        setAllAgents(response.agents);
        
        // Selecionar todos os agentes disponíveis
        const availableAgents = response.agents.filter(agent => agent.available);
        setSelectedAgents(availableAgents);
      }
    } catch (err) {
      console.error('Erro ao carregar agentes:', err);
    }
  };
  
  const loadOptimizationHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getOptimizationJobs();
      
      if (response.success) {
        setOptimizationHistory(response.jobs);
      } else {
        setError('Falha ao carregar histórico de otimizações');
      }
    } catch (err) {
      setError(`Erro: ${err.message}`);
      console.error('Erro ao carregar histórico:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleOptimizeRoutes = async (formData) => {
    try {
      setLoading(true);
      setError(null);
      
      // Atualizar o formData para usar apenas os agentes selecionados
      const updatedFormData = {
        ...formData,
        agents: selectedAgents
      };
      
      const response = await optimizeRoutes(updatedFormData);
      
      if (response && response.success) {
        setRoutes(response.routes);
        setEmptyMap(false);
      } else {
        setError('Falha ao otimizar rotas. Tente novamente.');
      }
    } catch (err) {
      setError(`Erro: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTestMapbox = async () => {
    try {
      setLoading(true);
      setMapboxStatus(null);
      setError(null);
      
      const response = await testMapboxConnection();
      
      if (response && response.success) {
        setMapboxStatus({
          connected: true,
          message: 'Conexão com Mapbox estabelecida com sucesso!'
        });
      } else {
        setMapboxStatus({
          connected: false,
          message: 'Falha ao conectar com o Mapbox. Verifique a chave API.'
        });
      }
    } catch (err) {
      setError(`Erro: ${err.message}`);
      setMapboxStatus({
        connected: false,
        message: `Falha ao conectar com o Mapbox: ${err.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAgentsChange = (agents) => {
    console.log('App: Recebendo agentes atualizados:', agents.length);
    
    // Verificar se há realmente modificações para evitar loops
    const agentsChanged = JSON.stringify(agents) !== JSON.stringify(allAgents);
    if (!agentsChanged) {
      console.log('App: Lista de agentes não mudou, ignorando atualização');
      return;
    }
    
    console.log('App: Atualizando estado com novos agentes');
    setAllAgents(agents);
    
    // Atualizar a seleção para manter apenas os agentes que ainda existem
    // E adicionar novos agentes disponíveis à seleção
    setSelectedAgents(prevSelected => {
      // Manter apenas agentes que ainda existem
      const existingSelected = prevSelected.filter(selected => 
        agents.some(agent => agent.id === selected.id)
      );
      
      // Adicionar novos agentes disponíveis
      const newAgents = agents.filter(agent => 
        agent.available && !prevSelected.some(selected => selected.id === agent.id)
      );
      
      return [...existingSelected, ...newAgents];
    });
  };

  const handleSelectedAgentsChange = (agents) => {
    console.log("App: Atualizando agentes selecionados:", agents?.length || 0);
    setSelectedAgents(agents || []);
  };

  const handleSaveSettings = (newSettings) => {
    setSettings(newSettings);
    // Salvar no localStorage para persistência
    localStorage.setItem('rotasmais_settings', JSON.stringify(newSettings));
    // Voltar para a tela principal
    setCurrentScreen('home');
  };

  // Navegação entre telas - com memo para evitar renderizações desnecessárias
  const switchToScreen = React.useCallback((screen) => {
    setCurrentScreen(screen);
  }, []);
  
  // Renderiza a navegação global
  const renderNavigation = () => (
    <nav className="main-nav">
      <ul>
        <li className={currentScreen === 'home' ? 'active' : ''}>
          <button onClick={() => switchToScreen('home')}>Rotas</button>
        </li>
        <li className={currentScreen === 'agents' ? 'active' : ''}>
          <button onClick={() => switchToScreen('agents')}>Agentes</button>
        </li>
        <li className={currentScreen === 'history' ? 'active' : ''}>
          <button onClick={() => switchToScreen('history')}>Histórico</button>
        </li>
        <li className={currentScreen === 'settings' ? 'active' : ''}>
          <button onClick={() => switchToScreen('settings')}>Configurações</button>
        </li>
        <li className="user-info">
          <span>{user?.tenant?.name || "Usuário " + user?.username}</span>
          <button onClick={handleLogout} className="logout-button">Sair</button>
        </li>
      </ul>
    </nav>
  );

  // Função para carregar uma otimização do histórico
  const loadHistoricalOptimization = async (jobId) => {
    try {
      setLoading(true);
      setError(null);
      
      // Buscar o job pelo ID
      const response = await getOptimizationJobs(jobId);
      
      if (response.success && response.job) {
        // Carregar as rotas resultantes
        if (response.job.result_data) {
          setRoutes(response.job.result_data);
          setEmptyMap(false);
          // Voltar para a tela principal para mostrar as rotas
          setCurrentScreen('home');
        } else {
          setError('Esta otimização não tem resultados disponíveis');
        }
      } else {
        setError('Falha ao carregar dados da otimização');
      }
    } catch (err) {
      setError(`Erro: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Renderiza o conteúdo atual baseado na tela selecionada
  const renderContent = () => {
    switch (currentScreen) {
      case 'agents':
        return (
          <div className="screen-container">
            <h1 className="screen-title">Gerenciamento de Agentes</h1>
            <div className="screen-description">
              <p>
                Gerencie os agentes que farão as rotas.
              </p>
            </div>
            <AgentsManager 
              standalone={true}
              onAgentsChange={handleAgentsChange}
              initialAgents={allAgents}
            />
          </div>
        );
      
      case 'history':
        return (
          <div className="screen-container">
            <h1 className="screen-title">Histórico de Roteirizações</h1>
            <div className="screen-description">
              <p>
                Veja todas as roteirizações realizadas anteriormente e carregue-as novamente quando necessário.
              </p>
            </div>
            
            {loading ? (
              <div className="loading">Carregando histórico...</div>
            ) : error ? (
              <div className="error-message">{error}</div>
            ) : (
              <div className="optimization-history">
                <table className="history-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Data</th>
                      <th>Status</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {optimizationHistory.length > 0 ? (
                      optimizationHistory.map(job => (
                        <tr key={job.id}>
                          <td>{job.id}</td>
                          <td>{new Date(job.created_at).toLocaleString('pt-BR')}</td>
                          <td>
                            <span className={`status status-${job.status}`}>
                              {job.status === 'completed' ? 'Concluído' : 
                               job.status === 'processing' ? 'Em processamento' : 
                               job.status === 'failed' ? 'Falhou' : 'Pendente'}
                            </span>
                          </td>
                          <td>
                            <button 
                              onClick={() => loadHistoricalOptimization(job.id)}
                              disabled={job.status !== 'completed' || loading}
                              className="load-history-button"
                            >
                              Carregar
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="no-data">
                          Nenhuma roteirização encontrada no histórico
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      
      case 'settings':
        return (
          <div className="screen-container">
            <h1 className="screen-title">Configurações</h1>
            <div className="screen-description">
              <p>
                Configure os padrões para facilitar o cadastro de endereços. 
                Defina a cidade padrão para preencher automaticamente ao digitar endereços.
              </p>
            </div>
            <Settings 
              standalone={true}
              settings={settings}
              onSaveSettings={handleSaveSettings}
            />
          </div>
        );
      
      default: // 'home'
        return (
          <>
            <header className="app-header">
              <h1>RotasMais - Otimização de Rotas</h1>
              {process.env.REACT_APP_ENV !== 'production' && (
                <button 
                  onClick={handleTestMapbox} 
                  disabled={loading} 
                  className="test-mapbox-button"
                >
                  Testar Conexão Mapbox
                </button>
              )}
            </header>
            
            <main className="app-main">
              <div className="input-section">
                <div className="workflow-container">
                  <section className="workflow-section input-section">
                    <h2>1. Adicione os pontos</h2>
                    <p>Cadastre os endereços que devem ser visitados</p>
                    <InputForm 
                      onSubmit={handleOptimizeRoutes} 
                      loading={loading} 
                      defaultSettings={settings}
                    />
                  </section>
                  
                  <section className="workflow-section agent-section">
                    <h2>2. Selecione os agentes</h2>
                    <p>Escolha quais agentes farão as visitas</p>
                    <AgentSelector 
                      selectedAgents={selectedAgents}
                      onAgentsChange={handleSelectedAgentsChange}
                    />
                  </section>
                </div>
                
                {process.env.REACT_APP_ENV !== 'production' && mapboxStatus && (
                  <div className={`mapbox-status ${mapboxStatus.connected ? 'success' : 'error'}`}>
                    {mapboxStatus.message}
                  </div>
                )}
              </div>
              
              <div className="results-section">
                {error && <div className="error-message">{error}</div>}
                
                <div className="map-container">
                  <React.Suspense fallback={<div>Carregando mapa...</div>}>
                    <MapComponent 
                      key={`map-${settings.defaultCity || 'default'}-${routes.length}`}
                      routes={emptyMap ? [] : routes} 
                      defaultSettings={settings}
                    />
                  </React.Suspense>
                </div>
                
                {routes.length > 0 && (
                  <div className="routes-list-container">
                    <RoutesList routes={routes} />
                  </div>
                )}
              </div>
            </main>
          </>
        );
    }
  };
  
  // Método para fazer logout
  const handleLogout = async () => {
    try {
      await logout();
      window.location.href = '/login';
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  // Componente com a estrutura principal do aplicativo
  const MainApp = () => (
    <div className="app">
      {renderNavigation()}
      {renderContent()}
      <footer className="app-footer">
        <p>&copy; {new Date().getFullYear()} RotasMais</p>
      </footer>
    </div>
  );

  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route 
          path="/*" 
          element={
            <PrivateRoute>
              <MainApp />
            </PrivateRoute>
          } 
        />
      </Routes>
    </Router>
  );
}

export default App;